import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react"

const LOCAL_STORAGE_CHANGE_EVENT = "local-storage-change"

export type StorageType = "localStorage" | "sessionStorage"

const getStorage = (storageType: StorageType): Storage | undefined => {
	if (typeof window === "undefined") {
		return undefined
	}

	return window[storageType]
}

export function useBrowserStorage(
	storageType: StorageType,
	key: string,
	defaultValue: string
): [string | null, Dispatch<SetStateAction<string | null>>] {
	const readStoredValue = useCallback(() => {
		const storage = getStorage(storageType)
		if (storage === undefined) {
			return defaultValue
		}

		try {
			const item = storage.getItem(key)
			return item !== null ? JSON.parse(item) : defaultValue
		} catch (error) {
			console.log(error)
			return defaultValue
		}
	}, [defaultValue, key, storageType])

	// State to store our value
	// Pass initial state function to useState so logic is only executed once
	const [storedValue, setStoredValue] = useState(() => readStoredValue())

	useEffect(() => {
		const storage = getStorage(storageType)
		if (storage === undefined) {
			return
		}

		const syncStoredValue = () => {
			setStoredValue(readStoredValue())
		}

		const handleCustomStorageChange = (event: Event) => {
			const customEvent = event as CustomEvent<{ key: string }>
			if (customEvent.detail.key !== key) {
				return
			}
			syncStoredValue()
		}

		const handleStorageChange = (event: StorageEvent) => {
			if (event.key !== key || event.storageArea !== storage) {
				return
			}
			syncStoredValue()
		}

		window.addEventListener("storage", handleStorageChange)
		window.addEventListener(LOCAL_STORAGE_CHANGE_EVENT, handleCustomStorageChange)

		return () => {
			window.removeEventListener("storage", handleStorageChange)
			window.removeEventListener(LOCAL_STORAGE_CHANGE_EVENT, handleCustomStorageChange)
		}
	}, [key, readStoredValue, storageType])

	// Return a wrapped version of useState's setter function that ...
	// ... persists the new value to browser storage.
	const setValue = useCallback(
		(value) => {
			try {
				setStoredValue((currentStoredValue) => {
					// Allow value to be a function so we have same API as useState
					const valueToStore =
						value instanceof Function ? value(currentStoredValue) : value

					// Save to browser storage
					const storage = getStorage(storageType)
					if (storage !== undefined) {
						storage.setItem(key, JSON.stringify(valueToStore))
						window.dispatchEvent(
							new CustomEvent(LOCAL_STORAGE_CHANGE_EVENT, {
								detail: { key },
							})
						)
					}

					return valueToStore
				})
			} catch (error) {
				// A more advanced implementation would handle the error case
				console.log(error)
			}
		},
		[key, storageType]
	)
	return [storedValue, setValue]
}

export default function useLocalStorage(
	key: string,
	defaultValue: string
): [string | null, Dispatch<SetStateAction<string | null>>] {
	return useBrowserStorage("localStorage", key, defaultValue)
}

export function useSessionStorage(
	key: string,
	defaultValue: string
): [string | null, Dispatch<SetStateAction<string | null>>] {
	return useBrowserStorage("sessionStorage", key, defaultValue)
}
